import React, { useCallback } from 'react';

import { useTranslation } from '@lyearn/i18n';
import { ArrowIosDownward } from '@lyearn/icons';
import { cn } from '@lyearn/style';
import { DatePickerDate, DateVariant, Stack, Typography } from '@lyearn/ui';
import DatePicker from '@/components/DatePicker';
import SortMenu from '@/components/SortMenu';
import { SortMenuProps } from '@/components/SortMenu/types';

import { getDefaultDeactivationDate } from '../Editor/config';
import { stopAssignMenuButtonWidth } from './styles';
import { AutoAssignEnum } from './types';
import { getAutoAssignMenu } from './utils';

interface StopAutoAssignProps {
  onStopAutoSelectSelect: (value?: string) => void;
  disabled?: boolean;
  deactivateFrom?: string | null;
}

const getDateSelectorValue = (deactivateFrom: string | null | undefined) => {
  if (deactivateFrom && deactivateFrom !== '-1') {
    return new Date(deactivateFrom);
  }
  return new Date(getDefaultDeactivationDate());
};

const AUTO_ASSIGN_DROPDOWN_OVERRIDES: SortMenuProps['overrides'] = {
  PopoverContent: {
    maxWidth: 29.4,
    position: 'bottom-end',
  },
  MenuItem: {
    Typography: {
      variant: 'body-short-01',
    },
  },
};

function StopAutoAssign(props: StopAutoAssignProps) {
  const { t } = useTranslation();
  const { deactivateFrom, onStopAutoSelectSelect, disabled } = props;
  const autoAssingMenuItem = getAutoAssignMenu(t);

  const selectedOption =
    deactivateFrom === '-1' ? AutoAssignEnum.Never : AutoAssignEnum.OnSpecificDate;
  const selectedOptionLabel = autoAssingMenuItem.find((item) => item.id === selectedOption)?.label;

  const dateSelectorValue = getDateSelectorValue(deactivateFrom);

  const onSelect = useCallback(
    (value: string) => {
      if (value === AutoAssignEnum.Never) {
        onStopAutoSelectSelect('-1');
      } else {
        onStopAutoSelectSelect(getDefaultDeactivationDate());
      }
    },
    [onStopAutoSelectSelect],
  );

  const onDateChange = useCallback(
    (date: DatePickerDate) => {
      onStopAutoSelectSelect(date?.toISOString());
    },
    [onStopAutoSelectSelect],
  );

  return (
    <>
      <Typography as="p" className="mb-12 pt-32 ms-8" variant="label-01">
        {t('AssignmentCreator:StopAutoAssign')}
      </Typography>
      <Stack className="max-w-[60rem]" gapX={12}>
        <SortMenu
          actions={autoAssingMenuItem}
          defaultSortBy={selectedOption}
          disabled={disabled}
          overrides={AUTO_ASSIGN_DROPDOWN_OVERRIDES}
          onSort={onSelect}>
          <Stack
            alignItems="center"
            className={cn(
              stopAssignMenuButtonWidth,
              'h-48 flex-shrink-0 rounded-8 border border-interactive-border px-16',
            )}
            justifyContent="between">
            <Typography variant="body-short-01">{selectedOptionLabel}</Typography>
            <ArrowIosDownward className="h-20 w-20" fillColorToken="icon-placeholder" />
          </Stack>
        </SortMenu>
        {deactivateFrom !== '-1' ? (
          <DatePicker
            disabled={disabled}
            pickerProps={{
              variant: DateVariant.Date,
              selectedTime: dateSelectorValue,
              onChange: onDateChange,
            }}
          />
        ) : null}
      </Stack>
    </>
  );
}

export default React.memo(StopAutoAssign);
